import { useMemo } from 'react'

//We will need to decide how to deal with fonts
enum Font {
  arial = 'Arial, "Helvetica Neue", Helvetica, sans-serif',
  arialBlack = '"Arial Black", "Arial Bold", Gadget, sans-serif',
  andaleMono = '"Andale Mono", AndaleMono, monospace',
  baskerVille = 'Baskerville, "Baskerville Old Face", "Hoefler Text", Garamond, "Times New Roman", serif',
  bodoniMt = '"Bodoni MT", Didot, "Didot LT STD", "Hoefler Text", Garamond, "Times New Roman", serif',
  brushScriptMt = '"Brush Script MT", cursive',
  calibri = 'Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif',
  candara = 'Segoe, "Segoe UI", Optima, Arial, sans-serif',
  centuryGothic = '"Century Gothic", CenturyGothic, AppleGothic, sans-serif',
  comicSansMs = 'Comic Sans MS',
  courierNew = 'Courier New, Courier, Lucida Sans Typewriter, Lucida Typewriter, monospace',
  didot = 'Didot, "Didot LT STD", "Hoefler Text", Garamond, "Times New Roman", serif',
  garamond = 'Garamond, Baskerville, "Baskerville Old Face", "Hoefler Text", "Times New Roman", serif',
  georgia = 'Georgia, Times, "Times New Roman", serif',
  gillSans = '"Gill Sans", "Gill Sans MT", Calibri, sans-serif',
  helvetica = '"Helvetica Neue", Helvetica, Arial, sans-serif',
  impact = 'Impact, Haettenschweiler, "Franklin Gothic Bold", Charcoal, "Helvetica Inserat", "Bitstream Vera Sans Bold", "Arial Black", sans serif',
  lucida = '"Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Geneva, Verdana, sans-serif',
  monaco = 'monaco, Consolas, Lucida Console, monospace',
  optima = 'Optima, Segoe, "Segoe UI", Candara, Calibri, Arial, sans-serif',
  outfit = 'Outfit',
  palatino = 'Palatino, "Palatino Linotype", "Palatino LT STD", "Book Antiqua", Georgia, serif',
  segoeUI = '"Segoe UI", Optima, Arial, sans-serif',
  tahoma = 'Tahoma, Verdana, Segoe, sans-serif',
  trebuchetMs = '"Trebuchet MS", "Lucida Grande", "Lucida Sans Unicode", "Lucida Sans", Tahoma, sans-serif',
  timesNewRoman = 'Times New Roman',
  verdana = 'Verdana, Geneva, sans-serif',
  verveineStdRg = 'Verveine Std Rg',
}

interface FontObject {
  fontKey: string
  font: string
  fontSize: string
  mobileFontSize?: string
}

const FOODBANK_FONTS = {
  dmSansRegular: '"DM Sans Regular"',
  dmSansBold: '"DM Sans Bold"',
  workSansExtraBold: '"Work Sans ExtraBold"',
}

const JNF_FONTS = {
  formataBold: '"Formata Bold"',
  formataRegular: '"Formata Regular"',
  univers: '"Univers"',
  museoSansCond300: '"Museo Sans Cond 300"',
  museoSansCond900: '"Museo Sans Cond 900"',
  museoSansRounded700: '"Museo Sans Rounded 700"',
}

const BOSA_FONTS = {
  montserrat: '"Montserrat"',
  montserratItalic: '"Montserrat Italic"',
}

const NEXTSENSE_FONTS = {
  nextSansBlack: '"Next Sans Black"',
  nextSansBlackItalic: '"Next Sans Black Italic"',
  nextSansBold: '"Next Sans Bold"',
  nextSansBoldItalic: '"Next Sans Bold Italic"',
  nextSansExtraBold: '"Next Sans ExtraBold"',
  nextSansExtraBoldItalic: '"Next Sans ExtraBold Italic"',
  nextSansItalic: '"Next Sans Italic"',
  nextSansLight: '"Next Sans Light"',
  nextSansLightItalic: '"Next Sans Light Italic"',
  nextSansMedium: '"Next Sans Medium"',
  nextSansMediumItalic: '"Next Sans Medium Italic"',
  nextSansRegular: '"Next Sans Regular"',
}

const VSK_FONTS = {
  montserrat: '"Montserrat"',
  montserratItalic: '"Montserrat Italic"',
  knileRegular: '"Knile Regular"',
  knileItalic: '"Knile Italic"',
  knileBlack: '"Knile Black"',
  knileBold: '"Knile Bold"',
  knileSemiBold: '"Knile SemiBold"',
}

const VACCA_FONTS = {
  ludicrous: '"Ludicrous"',
}

const NCBF_FONTS = {
  modernEraRegular: '"Modern Era Regular"',
  modernEraBlack: '"Modern Era Black"',
}

const KUC_FONTS = {
  rubikBlack: '"Rubik Black"',
  rubikBlackItalic: '"Rubik Black Italic"',
  rubikBold: '"Rubik Bold"',
  rubikBoldItalic: '"Rubik Bold Italic"',
  rubikExtraBold: '"Rubik ExtraBold"',
  rubikExtraBoldItalic: '"Rubik ExtraBold Italic"',
  rubikItalic: '"Rubik Italic"',
  rubikLight: '"Rubik Light"',
  rubikLightItalic: '"Rubik Light Italic"',
  rubikMedium: '"Rubik Medium"',
  rubikMediumItalic: '"Rubik Medium Italic"',
  rubikRegular: '"Rubik Regular"',
  rubikSemiBold: '"Rubik SemiBold"',
  rubikSemiBoldItalic: '"Rubik SemiBold Italic"',
}

const STB_FONTS = {
  barmenoBold: '"Barmeno Bold"',
  barmenoRegular: '"Barmeno Regular"',
}

const JCA_FONTS = {
  sofiaProRegular: 'Sofia Pro Regular',
  sofiaProLight: 'Sofia Pro Light',
  sofiaProUltraLight: 'Sofia Pro Ultra Light',
  sofiaProMedium: 'Sofia Pro Medium',
  sofiaProBold: 'Sofia Pro Bold',
  sofiaProExtraLight: 'Sofia Pro Extra Light',
  sofiaProSemiBold: 'Sofia Pro Semi Bold',
  sofiaProBlack: 'Sofia Pro Black',
}

const UIA_FONTS = {
  nunitoSansBlack: 'Nunito Sans Black',
  nunitoSansBlackItalic: 'Nunito Sans Black Italic',
  nunitoSansBold: 'Nunito Sans Bold',
  nunitoSansBoldItalic: 'Nunito Sans Bold Italic',
  nunitoSansExtraBold: 'Nunito Sans Extra Bold',
  nunitoSansExtraBoldItalic: 'Nunito Sans Extra Bold Italic',
  nunitoSansExtraLight: 'Nunito Sans Extra Light',
  nunitoSansExtraLightItalic: 'Nunito Sans Extra Light Italic',
  nunitoSansItalic: 'Nunito Sans Italic',
  nunitoSansLight: 'Nunito Sans Light',
  nunitoSansLightItalic: 'Nunito Sans Light Italic',
  nunitoSansRegular: 'Nunito Sans Regular',
  nunitoSansSemiBold: 'Nunito Sans Semi Bold',
  nunitoSansSemiBoldItalic: 'Nunito Sans Semi Bold Italic',
}

const ARTS_CENTRE_FONTS = {
  avertaStdBlack: 'Averta Std Black',
  avertaStdBlackItalic: 'Averta Std Black Italic',
  avertaStdBold: 'Averta Std Bold',
  avertaStdBoldItalic: 'Averta Std Bold Italic',
  avertaStdExtraBold: 'Averta Std Extra Bold',
  avertaStdExtraBoldItalic: 'Averta Std Extra Bold Italic',
  avertaStdExtraThin: 'Averta Std Extra Thin',
  avertaStdExtraThinItalic: 'Averta Std Extra Thin Italic',
  avertaStdLight: 'Averta Std Light',
  avertaStdLightItalic: 'Averta Std Light Italic',
  avertaStdRegular: 'Averta Std Regular',
  avertaStdRegularItalic: 'Averta Std Regular Italic',
  avertaStdSemiBold: 'Averta Std Semi Bold',
  avertaStdSemiBoldItalic: 'Averta Std Semi Bold Italic',
  avertaStdThin: 'Averta Std Thin',
  avertaStdThinItalic: 'Averta Std Thin Italic',
}

const YOTS_FONTS = {
  tinyGroteskBold: 'Tiny Grotesk Bold',
  tinyGroteskLight: 'Tiny Grotesk Light',
  tinyGroteskMedium: 'Tiny Grotesk Medium',
  tinyGroteskRegular: 'Tiny Grotesk Regular',
}

const FLOREY_FONTS = {
  concretteBold: 'ConcretteM Bold',
  concretteLight: 'ConcretteM Light',
  concretteMedium: 'ConcretteM Medium',
  gilroyBlack: 'Gilroy Black',
  gilroyBlackItalic: 'Gilroy Black Italic',
  gilroyBold: 'Gilroy Bold',
  gilroyBoldItalic: 'Gilroy Bold Italic',
  gilroyExtraBold: 'Gilroy ExtraBold',
  gilroyExtraBoldItalic: 'Gilroy ExtraBold Italic',
  gilroyHeavy: 'Gilroy Heavy',
  gilroyHeavyItalic: 'Gilroy Heavy Italic',
  gilroyLight: 'Gilroy Light',
  gilroyLightItalic: 'Gilroy Light Italic',
  gilroyMedium: 'Gilroy Medium',
  gilroyMediumItalic: 'Gilroy Medium Italic',
  gilroyRegular: 'Gilroy Regular',
  gilroyRegularItalic: 'Gilroy Regular Italic',
  gilroySemiBold: 'Gilroy SemiBold',
  gilroySemiBoldItalic: 'Gilroy SemiBold Italic',
  gilroyThin: 'Gilroy Thin',
  gilroyThinItalic: 'Gilroy Thin Italic',
  gilroyUltraLight: 'Gilroy UltraLight',
  gilroyUltraLightItalic: 'Gilroy UltraLight Italic',
}

const mapFont = (font: [string, string]) => ({
  fontKey: font[0],
  font: font[1],
  fontSize: '16px',
})

const FONTS: FontObject[] = Object.entries(Font).map(mapFont)

const FOODBANK_CHARITY_ID = ['e9492f21-0934-4cf9-801f-875353a6e96b', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const JNF_CHARITY_ID = ['258fc211-b750-432e-9720-742f87a0f06d', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const BOSA_CHARITY_ID = ['8a0b0980-fe44-4bf6-8602-072fedabddb1', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const NEXTSENSE_CHARITY_ID = ['55360769-61e8-4090-9689-4e102bb76a3f', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const VSK_CHARITY_ID = ['52a462fe-adcd-4f32-a4ff-a866f896644f', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const VACCA_CHARITY_ID = ['e2a76b88-13b2-455f-85e3-0e0bdcc05228', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const NCBF_CHARITY_ID = ['7eb127d1-2af6-4118-be9c-ea965efa8e92', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const KUC_CHARITY_ID = ['fb5f7a4e-581c-48ff-ba86-f66c77a62244', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const STB_CHARITY_ID = ['5b4591ef-95a1-445b-97bc-8e1a9b457e01', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const JCA_CHARITY_ID = ['821cb96e-b5c7-460d-af5b-a3366c79cf30', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const UIA_CHARITY_ID = ['b97fbb1c-4746-40a5-98c3-f024f15fbc46', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const ARTS_CENTRE_CHARITY_ID = ['57cd8574-2f34-4d6f-8991-8bfe1332de43', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const YOTS_CHARITY_ID = ['fc4a8541-8506-49ed-8aaf-d3d57cbc4390', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']
const FLOREY_CHARITY_ID = ['213113a7-34da-46f9-967f-920318264d67', '24cdb2be-cd63-46e2-9904-7d669dfa52e6']

const getCharityFonts = (charityId: string): FontObject[] => [
  ...(BOSA_CHARITY_ID.includes(charityId) ? Object.entries(BOSA_FONTS).map(mapFont) : []),
  ...(JNF_CHARITY_ID.includes(charityId) ? Object.entries(JNF_FONTS).map(mapFont) : []),
  ...(FOODBANK_CHARITY_ID.includes(charityId) ? Object.entries(FOODBANK_FONTS).map(mapFont) : []),
  ...(NEXTSENSE_CHARITY_ID.includes(charityId) ? Object.entries(NEXTSENSE_FONTS).map(mapFont) : []),
  ...(VSK_CHARITY_ID.includes(charityId) ? Object.entries(VSK_FONTS).map(mapFont) : []),
  ...(VACCA_CHARITY_ID.includes(charityId) ? Object.entries(VACCA_FONTS).map(mapFont) : []),
  ...(NCBF_CHARITY_ID.includes(charityId) ? Object.entries(NCBF_FONTS).map(mapFont) : []),
  ...(KUC_CHARITY_ID.includes(charityId) ? Object.entries(KUC_FONTS).map(mapFont) : []),
  ...(STB_CHARITY_ID.includes(charityId) ? Object.entries(STB_FONTS).map(mapFont) : []),
  ...(JCA_CHARITY_ID.includes(charityId) ? Object.entries(JCA_FONTS).map(mapFont) : []),
  ...(UIA_CHARITY_ID.includes(charityId) ? Object.entries(UIA_FONTS).map(mapFont) : []),
  ...(ARTS_CENTRE_CHARITY_ID.includes(charityId) ? Object.entries(ARTS_CENTRE_FONTS).map(mapFont) : []),
  ...(YOTS_CHARITY_ID.includes(charityId) ? Object.entries(YOTS_FONTS).map(mapFont) : []),
  ...(FLOREY_CHARITY_ID.includes(charityId) ? Object.entries(FLOREY_FONTS).map(mapFont) : []),
  ...FONTS,
]

const useCharityFonts = () => {
  const charityId = localStorage.getItem('ge_selected_charity_id')
  return useMemo(() => (charityId ? getCharityFonts(charityId) : FONTS), [charityId])
}

const getFontKeyFromValue = (value: string) => {
  return [
    ...Object.entries(Font),
    ...Object.entries(FOODBANK_FONTS),
    ...Object.entries(JNF_FONTS),
    ...Object.entries(BOSA_FONTS),
    ...Object.entries(NEXTSENSE_FONTS),
    ...Object.entries(VSK_FONTS),
    ...Object.entries(VACCA_FONTS),
    ...Object.entries(NCBF_FONTS),
    ...Object.entries(KUC_FONTS),
    ...Object.entries(STB_FONTS),
    ...Object.entries(JCA_FONTS),
    ...Object.entries(UIA_FONTS),
    ...Object.entries(ARTS_CENTRE_FONTS),
    ...Object.entries(YOTS_FONTS),
    ...Object.entries(FLOREY_FONTS),
  ].find(([, keyValue]) => keyValue === value)?.[0]
}

export { Font, FONTS, getCharityFonts, useCharityFonts, getFontKeyFromValue, mapFont, type FontObject }
